// src/pages/admin/Posts/Tags.js
import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Modal, Form, message, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const Tags = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [filteredTags, setFilteredTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [form] = Form.useForm();

  // Sample initial tags (replace with API fetch in production)
  const initialTags = [
    { id: 1, name: 'AI', description: 'Artificial Intelligence related posts', status: 'Active' },
    { id: 2, name: 'Cybersecurity', description: 'Security and threat detection', status: 'Active' },
    { id: 3, name: 'Data Science', description: 'Data analysis and insights', status: 'Active' },
    { id: 4, name: 'Cloud', description: 'Cloud computing topics', status: 'Inactive' },
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setTags(initialTags);
      setFilteredTags(initialTags);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    fetch('/api/tags')
      .then((response) => response.json())
      .then((data) => {
        setTags(data);
        setFilteredTags(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching tags:', error);
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = tags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(value.toLowerCase()) ||
        tag.description.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredTags(filtered);
    setCurrentPage(1);
  };

  const handleAddTag = () => {
    setIsAddModalVisible(true);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    form.setFieldsValue(tag);
    setIsEditModalVisible(true);
  };

  const handleDeleteTag = (tagId) => {
    const tag = tags.find((t) => t.id === tagId);
    Modal.confirm({
      title: 'Delete Tag',
      content: `Are you sure you want to delete the tag "${tag.name}"?`,
      onOk: () => {
        const updatedTags = tags.filter((t) => t.id !== tagId);
        setTags(updatedTags);
        setFilteredTags(updatedTags);
        message.success(`Tag "${tag.name}" deleted successfully`);
      },
    });
  };

  const handleToggleStatus = (tagId) => {
    const tag = tags.find((t) => t.id === tagId);
    const newStatus = tag.status === 'Active' ? 'Inactive' : 'Active';
    Modal.confirm({
      title: `${newStatus === 'Inactive' ? 'Deactivate' : 'Activate'} Tag`,
      content: `Are you sure you want to ${newStatus.toLowerCase()} the tag "${tag.name}"?`,
      onOk: () => {
        const updatedTags = tags.map((t) =>
          t.id === tagId ? { ...t, status: newStatus } : t
        );
        setTags(updatedTags);
        setFilteredTags(updatedTags);
        message.success(`Tag "${tag.name}" ${newStatus.toLowerCase()}d successfully`);
      },
    });
  };

  const handleAddTagSubmit = (values) => {
    const newTag = {
      id: tags.length + 1,
      name: values.name,
      description: values.description,
      status: 'Active',
    };
    const updatedTags = [...tags, newTag];
    setTags(updatedTags);
    setFilteredTags(updatedTags);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success(`Tag "${newTag.name}" added successfully`);
  };

  const handleEditTagSubmit = (values) => {
    const updatedTags = tags.map((t) =>
      t.id === selectedTag.id ? { ...t, ...values } : t
    );
    setTags(updatedTags);
    setFilteredTags(updatedTags);
    setIsEditModalVisible(false);
    form.resetFields();
    message.success(`Tag "${values.name}" updated successfully`);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      const sorted = [...filteredTags].sort((a, b) => {
        const valueA = a[sorter.field].toLowerCase();
        const valueB = b[sorter.field].toLowerCase();
        return sorter.order === 'ascend' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      });
      setFilteredTags(sorted);
    }
  };

  const columns = [
    {
      title: 'Tag Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button onClick={() => handleEditTag(record)}>Edit</Button>
          <Button
            danger={record.status === 'Active'}
            onClick={() => handleToggleStatus(record.id)}
          >
            {record.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button danger onClick={() => handleDeleteTag(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <Title level={2} style={{ marginBottom: 16 }}>Manage Tags</Title>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Input
          placeholder="Search tags..."
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Button type="primary" onClick={handleAddTag}>
          Add Tag
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredTags}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filteredTags.length,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />

      <Modal
        title="Add New Tag"
        open={isAddModalVisible} // Changed from 'visible' for Ant Design v5 compatibility
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddTagSubmit}>
          <Form.Item
            name="name"
            label="Tag Name"
            rules={[{ required: true, message: 'Please enter the tag name' }]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Enter tag description" rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Tag
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsAddModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Edit Tag: ${selectedTag?.name || ''}`}
        open={isEditModalVisible} // Changed from 'visible'
        onCancel={() => {
          setIsEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleEditTagSubmit}>
          <Form.Item
            name="name"
            label="Tag Name"
            rules={[{ required: true, message: 'Please enter the tag name' }]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Enter tag description" rows={4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsEditModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Tags;